/**
 * Tax settings WRITE endpoints — request schemas (Pattern B).
 *
 * Served by `apps/api/src/routes/accounting/tax-settings.ts` under
 * `/api/v1/tax-settings`. Writes to `organization_tax_settings` and
 * `tax_rates` are backend-only once
 * `20261001070733_accounting_settings_backend_write_lockdown.sql` is pushed:
 * RESTRICTIVE `verify_backend_token()` policies AND with the org RLS, which
 * keeps doing the authorization (admin-only for the settings row, member for
 * the rates). See docs/BACKEND_API.md → "Pattern B" and docs/TAX.md.
 *
 * Every schema is `.strict()` and none accepts `organization_id`: the org is
 * the validated `:orgId` route param.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/** The `organization_tax_settings_tax_calculation_method_check` values. */
export const taxCalculationMethodSchema = z.enum(["invoice_based", "payment_based"]);
/**
 * A town / city slug (`burlington`, `rutland-town`) — the
 * `tax_rate_templates_locality_code_format_check` pattern. Whether the code is
 * a known town of the org's jurisdiction is `sync_org_tax_rates`' call (an
 * unknown code RAISES 22023, surfaced as a 400 by the settings PUT).
 */
export const taxLocalityCodeSchema = z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, { message: "Invalid town code" });
/** `tax_rate_templates_tax_category_check`. */
export const taxCategorySchema = z.enum(["sales", "meals", "rooms", "alcohol"]);
/** `tax_rate_templates_collected_by_check` — who collects a local share. */
export const taxCollectorSchema = z.enum(["state", "city"]);
/**
 * PUT /orgs/:orgId/settings — upsert the org's single tax-settings row
 * (unique on `organization_id`). The first save INSERTs, which fires the
 * DEFINER trigger `trg_setup_tax_accounts` (tax GL accounts + system rates for
 * the jurisdiction); later saves UPDATE.
 */
export const upsertTaxSettingsSchema = z
    .object({
    primary_tax_jurisdiction: z.string().min(1).max(20),
    tax_identification_number: z.string().max(100).nullable().optional(),
    fiscal_year_start_month: z.number().int().min(1).max(12).nullable().optional(),
    tax_calculation_method: taxCalculationMethodSchema.nullable().optional(),
    /** First day of the first period filed from Relevé, or null (no fence). */
    vat_filing_start_date: calendarDateSchema.nullable().optional(),
    /**
     * The towns the org operates in (`organization_tax_settings.tax_locality_codes`,
     * 20261028100300). A change fires `trg_setup_tax_accounts` →
     * `sync_org_tax_rates`, which adds the towns' combined state + local rates
     * and expires those of a removed town. Omitted = leave the stored list
     * alone (same partial-body rule as every other key here).
     */
    tax_locality_codes: z
        .array(taxLocalityCodeSchema)
        .max(100)
        .refine((codes) => new Set(codes).size === codes.length, {
        message: "Each town can be listed once",
    })
        .optional(),
})
    .strict();
/** `tax_rates_tax_type_check`, as accepted by `create_custom_tax_rate`. */
export const taxRateTypeSchema = z.enum(["VAT", "SALES_TAX", "FRINGE_BENEFIT", "WITHHOLDING"]);
/**
 * The liability account a custom SALES_TAX rate posts to
 * (`create_custom_tax_rate`'s `p_target_account_type`, 20261028100300):
 * Sales Tax Payable (the default) or Meals & Rooms Tax Payable (a Vermont
 * meals / rooms / alcohol-type rate). Its report section follows the account.
 */
export const salesTaxTargetAccountTypeSchema = z.enum([
    "sales_tax_payable",
    "meals_rooms_tax_payable",
]);
/**
 * POST /orgs/:orgId/rates — `create_custom_tax_rate` (custom, non-system rate).
 *
 * A SALES_TAX rate is sales-side only: US sales tax paid on a purchase is part
 * of its cost (the purchase is recorded with "No tax"), and an INPUT sales-tax
 * rate would debit Sales Tax Payable. `create_custom_tax_rate` refuses it too
 * (20261027160000); this refusal just answers before the RPC is reached.
 *
 * `target_account_type` is for a SALES_TAX OUTPUT rate only (the RPC refuses
 * it on anything else); omitted = Sales Tax Payable.
 */
export const createCustomTaxRateSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    rate: z.number().min(0).max(100),
    direction: z.enum(["OUTPUT", "INPUT"]),
    tax_type: taxRateTypeSchema.optional(),
    is_recoverable: z.boolean().optional(),
    target_account_type: salesTaxTargetAccountTypeSchema.optional(),
})
    .strict()
    .refine((body) => !(body.tax_type === "SALES_TAX" && body.direction === "INPUT"), {
    message: "Sales tax rates can only be added for sales (direction OUTPUT)",
    path: ["direction"],
})
    .refine((body) => body.target_account_type === undefined ||
    (body.tax_type === "SALES_TAX" && body.direction === "OUTPUT"), {
    message: "An account can only be chosen for a sales tax rate on sales",
    path: ["target_account_type"],
});
/** PUT /orgs/:orgId/default-rate — `set_default_tax_rate` (one OUTPUT default). */
export const setDefaultTaxRateSchema = z
    .object({
    tax_rate_id: z.string().uuid(),
})
    .strict();
/** GET /localities query. */
export const taxLocalitiesQuerySchema = z
    .object({
    jurisdiction: z.string().min(1).max(20),
})
    .strict();
//# sourceMappingURL=taxSettings.js.map